import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import ContactName from "@/components/access-contacts/contact-name-left-section/ContactName";
import Contact from "@/components/access-contacts/contact-right-section/Contact";
import Interaction from "@/components/access-contacts/interaction-middle-section/Interaction";
import { useContactDetails } from "@/hooks/useContactDetails";
import { isValidUUID } from "@/utils/contactUtils";

const AccessContacts: React.FC = () => {
  const { contactId } = useParams<{ contactId: string }>();
  const navigate = useNavigate();

  // Validar UUID antes de tentar carregar contato
  if (contactId && !isValidUUID(contactId)) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-4">
        <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-neutral-50">ID de contato inválido</h3>
        <p className="text-neutral-400 text-center max-w-md">
          O ID fornecido não é válido. Por favor, verifique o link e tente novamente.
        </p>
        <button
          onClick={() => navigate('/crm')}
          className="flex items-center gap-2 px-4 py-2 bg-neutral-600 hover:bg-neutral-700 text-neutral-50 rounded-md transition-colors mt-4"
        >
          <ArrowLeft size={16} />
          Voltar para CRM
        </button>
      </div>
    );
  }

  const { contact, loading, error, updateContact, addInteraction, updateInteraction, deleteInteraction, refresh } = useContactDetails(contactId);

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-neutral-600 border-t-neutral-50 rounded-full animate-spin"></div>
          <p className="text-neutral-400">Carregando contato...</p>
        </div>
      </div>
    );
  }

  if (error || !contact) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-4">
        <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-neutral-50">Contato não encontrado</h3>
        <p className="text-neutral-400 text-center max-w-md">
          {error || 'Não foi possível encontrar este contato. Ele pode ter sido removido ou você não tem permissão para acessá-lo.'}
        </p>
        <button
          onClick={() => navigate('/crm')}
          className="flex items-center gap-2 px-4 py-2 bg-neutral-600 hover:bg-neutral-700 text-neutral-50 rounded-md transition-colors mt-4"
        >
          <ArrowLeft size={16} />
          Voltar para CRM
        </button>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-4">
      <button
        onClick={() => navigate('/crm')}
        className="flex items-center gap-2 text-neutral-400 hover:text-neutral-200 transition-colors"
      >
        <ArrowLeft size={16} />
        Voltar para CRM
      </button>

      <div className="w-full grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[1fr_2fr_1fr] gap-4">
        <ContactName contact={contact} onUpdate={updateContact} />
        <Interaction
          contact={contact}
          interactions={contact.interactions}
          onAddInteraction={addInteraction}
          onUpdateInteraction={updateInteraction}
          onDeleteInteraction={deleteInteraction}
        />
        <Contact contact={contact} onUpdate={updateContact} onRefresh={refresh} />
      </div>
    </div>
  );
};

export default AccessContacts;
